import React, { useEffect, useRef, useState, useCallback } from 'react';

const GraphView = ({ people, capabilities, coverageCalculator }) => {
  const networkRef = useRef(null);
  const [filters, setFilters] = useState({
    showPeople: true,
    showCapabilities: true,
    showSkills: true,
    showConnections: true,
    selectedPerson: null,
    selectedCapability: null,
    selectedSkill: null,
    minCoverage: 0
  });
  const [dimensions, setDimensions] = useState({ width: 1000, height: 600 });
  const [selectedNode, setSelectedNode] = useState(null);
  const [layout, setLayout] = useState('circular');
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Dynamic sizing based on container
  const updateDimensions = useCallback(() => {
    if (networkRef.current && networkRef.current.parentElement) {
      const container = networkRef.current.parentElement;
      const rect = container.getBoundingClientRect();
      setDimensions({
        width: Math.max(800, rect.width - 32), // Account for padding
        height: Math.max(600, Math.min(window.innerHeight - 300, 800)) // Dynamic height
      });
    }
  }, []);

  useEffect(() => {
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [updateDimensions]);

  useEffect(() => {
    createNetwork();
  }, [people, capabilities, filters, dimensions, layout]); // eslint-disable-line react-hooks/exhaustive-deps

  // Layout algorithms
  const calculateLayout = (filteredPeople, filteredCapabilities, filteredSkills) => {
    const centerX = dimensions.width / 2;
    const centerY = dimensions.height / 2;

    switch (layout) {
      case 'circular':
        return calculateCircularLayout(filteredPeople, filteredCapabilities, filteredSkills, centerX, centerY);
      case 'pyramid':
        return calculatePyramidLayout(filteredPeople, filteredCapabilities, filteredSkills, centerX, centerY);
      case 'grid':
        return calculateGridLayout(filteredPeople, filteredCapabilities, filteredSkills, centerX, centerY);
      case 'hierarchical':
        return calculateHierarchicalLayout(filteredPeople, filteredCapabilities, filteredSkills, centerX, centerY);
      case 'radial':
        return calculateRadialLayout(filteredPeople, filteredCapabilities, filteredSkills, centerX, centerY);
      case 'force':
        return calculateForceLayout(filteredPeople, filteredCapabilities, filteredSkills, centerX, centerY);
      default:
        return calculateCircularLayout(filteredPeople, filteredCapabilities, filteredSkills, centerX, centerY);
    }
  };

  const calculateCircularLayout = (filteredPeople, filteredCapabilities, filteredSkills, centerX, centerY) => {
    const nodes = [];
    const radius = Math.min(dimensions.width, dimensions.height) / 3;

    // People in outer circle
    if (filters.showPeople) {
      filteredPeople.forEach((person, index) => {
        const angle = (index / filteredPeople.length) * 2 * Math.PI;
        const x = centerX + radius * Math.cos(angle);
        const y = centerY + radius * Math.sin(angle);
        
        nodes.push({
          id: `person-${person.name}`,
          label: person.name,
          type: 'person',
          x: x,
          y: y,
          data: person,
          size: Math.max(20, (person.skills?.length || 0) * 3 + 15),
          color: '#3B82F6'
        });
      });
    }

    // Capabilities in middle circle
    if (filters.showCapabilities) {
      filteredCapabilities.forEach((capability, index) => {
        const angle = (index / filteredCapabilities.length) * 2 * Math.PI + Math.PI / 3;
        const x = centerX + (radius * 0.6) * Math.cos(angle);
        const y = centerY + (radius * 0.6) * Math.sin(angle);
        
        const teamCoverage = coverageCalculator.getTeamCoverage(people, capability);
        
        nodes.push({
          id: `capability-${capability.name}`,
          label: capability.name,
          type: 'capability',
          x: x,
          y: y,
          data: capability,
          size: Math.max(15, teamCoverage / 4 + 20),
          color: teamCoverage >= 80 ? '#10B981' : teamCoverage >= 50 ? '#F59E0B' : '#EF4444'
        });
      });
    }

    // Skills in inner circle
    if (filters.showSkills) {
      filteredSkills.forEach((skill, index) => {
        const angle = (index / filteredSkills.length) * 2 * Math.PI + Math.PI / 6;
        const x = centerX + (radius * 0.25) * Math.cos(angle);
        const y = centerY + (radius * 0.25) * Math.sin(angle);
        
        const peopleWithSkill = people.filter(p => p.skills && p.skills.includes(skill)).length;
        const adoptionRate = people.length > 0 ? (peopleWithSkill / people.length) * 100 : 0;
        
        nodes.push({
          id: `skill-${skill}`,
          label: skill,
          type: 'skill',
          x: x,
          y: y,
          data: { skill, adoptionRate, peopleWithSkill },
          size: Math.max(10, adoptionRate / 5 + 12),
          color: adoptionRate >= 75 ? '#8B5CF6' : adoptionRate >= 50 ? '#F59E0B' : '#6B7280'
        });
      });
    }

    return nodes;
  };

  const calculatePyramidLayout = (filteredPeople, filteredCapabilities, filteredSkills, centerX, centerY) => {
    const nodes = [];
    const pyramidHeight = dimensions.height * 0.7;
    const pyramidWidth = dimensions.width * 0.8;

    // Skills at the top (smallest layer)
    if (filters.showSkills) {
      const skillsPerRow = Math.ceil(Math.sqrt(filteredSkills.length));
      filteredSkills.forEach((skill, index) => {
        const row = Math.floor(index / skillsPerRow);
        const col = index % skillsPerRow;
        const rowWidth = pyramidWidth * 0.3;
        const x = centerX - rowWidth / 2 + (col * (rowWidth / (skillsPerRow - 1 || 1)));
        const y = centerY - pyramidHeight / 2 + row * 40;
        
        const peopleWithSkill = people.filter(p => p.skills && p.skills.includes(skill)).length;
        const adoptionRate = people.length > 0 ? (peopleWithSkill / people.length) * 100 : 0;
        
        nodes.push({
          id: `skill-${skill}`,
          label: skill,
          type: 'skill',
          x: x,
          y: y,
          data: { skill, adoptionRate, peopleWithSkill },
          size: Math.max(10, adoptionRate / 5 + 12),
          color: adoptionRate >= 75 ? '#8B5CF6' : adoptionRate >= 50 ? '#F59E0B' : '#6B7280'
        });
      });
    }

    // Capabilities in the middle
    if (filters.showCapabilities) {
      const capsPerRow = Math.ceil(Math.sqrt(filteredCapabilities.length));
      filteredCapabilities.forEach((capability, index) => {
        const row = Math.floor(index / capsPerRow);
        const col = index % capsPerRow;
        const rowWidth = pyramidWidth * 0.6;
        const x = centerX - rowWidth / 2 + (col * (rowWidth / (capsPerRow - 1 || 1)));
        const y = centerY - 50 + row * 60;
        
        const teamCoverage = coverageCalculator.getTeamCoverage(people, capability);
        
        nodes.push({
          id: `capability-${capability.name}`,
          label: capability.name,
          type: 'capability',
          x: x,
          y: y,
          data: capability,
          size: Math.max(15, teamCoverage / 4 + 20),
          color: teamCoverage >= 80 ? '#10B981' : teamCoverage >= 50 ? '#F59E0B' : '#EF4444'
        });
      });
    }

    // People at the base (largest layer)
    if (filters.showPeople) {
      const peoplePerRow = Math.ceil(Math.sqrt(filteredPeople.length));
      filteredPeople.forEach((person, index) => {
        const row = Math.floor(index / peoplePerRow);
        const col = index % peoplePerRow;
        const rowWidth = pyramidWidth;
        const x = centerX - rowWidth / 2 + (col * (rowWidth / (peoplePerRow - 1 || 1)));
        const y = centerY + pyramidHeight / 3 + row * 80;
        
        nodes.push({
          id: `person-${person.name}`,
          label: person.name,
          type: 'person',
          x: x,
          y: y,
          data: person,
          size: Math.max(20, (person.skills?.length || 0) * 3 + 15),
          color: '#3B82F6'
        });
      });
    }

    return nodes;
  };

  const calculateGridLayout = (filteredPeople, filteredCapabilities, filteredSkills, centerX, centerY) => {
    const nodes = [];
    const allNodes = [];
    
    // Collect all nodes
    if (filters.showPeople) {
      filteredPeople.forEach(person => {
        allNodes.push({
          id: `person-${person.name}`,
          label: person.name,
          type: 'person',
          data: person,
          size: Math.max(20, (person.skills?.length || 0) * 3 + 15),
          color: '#3B82F6'
        });
      });
    }

    if (filters.showCapabilities) {
      filteredCapabilities.forEach(capability => {
        const teamCoverage = coverageCalculator.getTeamCoverage(people, capability);
        allNodes.push({
          id: `capability-${capability.name}`,
          label: capability.name,
          type: 'capability',
          data: capability,
          size: Math.max(15, teamCoverage / 4 + 20),
          color: teamCoverage >= 80 ? '#10B981' : teamCoverage >= 50 ? '#F59E0B' : '#EF4444'
        });
      });
    }

    if (filters.showSkills) {
      filteredSkills.forEach(skill => {
        const peopleWithSkill = people.filter(p => p.skills && p.skills.includes(skill)).length;
        const adoptionRate = people.length > 0 ? (peopleWithSkill / people.length) * 100 : 0;
        
        allNodes.push({
          id: `skill-${skill}`,
          label: skill,
          type: 'skill',
          data: { skill, adoptionRate, peopleWithSkill },
          size: Math.max(10, adoptionRate / 5 + 12),
          color: adoptionRate >= 75 ? '#8B5CF6' : adoptionRate >= 50 ? '#F59E0B' : '#6B7280'
        });
      });
    }

    // Arrange in grid
    const cols = Math.ceil(Math.sqrt(allNodes.length));
    const rows = Math.ceil(allNodes.length / cols);
    const cellWidth = dimensions.width * 0.8 / cols;
    const cellHeight = dimensions.height * 0.8 / rows;
    const startX = centerX - (cols * cellWidth) / 2;
    const startY = centerY - (rows * cellHeight) / 2;

    allNodes.forEach((node, index) => {
      const row = Math.floor(index / cols);
      const col = index % cols;
      const x = startX + col * cellWidth + cellWidth / 2;
      const y = startY + row * cellHeight + cellHeight / 2;
      
      nodes.push({
        ...node,
        x: x,
        y: y
      });
    });

    return nodes;
  };

  const calculateHierarchicalLayout = (filteredPeople, filteredCapabilities, filteredSkills, centerX, centerY) => {
    const nodes = [];
    const levelHeight = dimensions.height * 0.8 / 3;
    const startY = centerY - dimensions.height * 0.3;

    // Level 1: Skills (top)
    if (filters.showSkills) {
      const skillWidth = dimensions.width * 0.8 / (filteredSkills.length || 1);
      filteredSkills.forEach((skill, index) => {
        const x = dimensions.width * 0.1 + index * skillWidth + skillWidth / 2;
        const y = startY;
        
        const peopleWithSkill = people.filter(p => p.skills && p.skills.includes(skill)).length;
        const adoptionRate = people.length > 0 ? (peopleWithSkill / people.length) * 100 : 0;
        
        nodes.push({
          id: `skill-${skill}`,
          label: skill,
          type: 'skill',
          x: x,
          y: y,
          data: { skill, adoptionRate, peopleWithSkill },
          size: Math.max(10, adoptionRate / 5 + 12),
          color: adoptionRate >= 75 ? '#8B5CF6' : adoptionRate >= 50 ? '#F59E0B' : '#6B7280'
        });
      });
    }

    // Level 2: Capabilities (middle)
    if (filters.showCapabilities) {
      const capWidth = dimensions.width * 0.8 / (filteredCapabilities.length || 1);
      filteredCapabilities.forEach((capability, index) => {
        const x = dimensions.width * 0.1 + index * capWidth + capWidth / 2;
        const y = startY + levelHeight;
        
        const teamCoverage = coverageCalculator.getTeamCoverage(people, capability);
        
        nodes.push({
          id: `capability-${capability.name}`,
          label: capability.name,
          type: 'capability',
          x: x,
          y: y,
          data: capability,
          size: Math.max(15, teamCoverage / 4 + 20),
          color: teamCoverage >= 80 ? '#10B981' : teamCoverage >= 50 ? '#F59E0B' : '#EF4444'
        });
      });
    }

    // Level 3: People (bottom)
    if (filters.showPeople) {
      const personWidth = dimensions.width * 0.8 / (filteredPeople.length || 1);
      filteredPeople.forEach((person, index) => {
        const x = dimensions.width * 0.1 + index * personWidth + personWidth / 2;
        const y = startY + levelHeight * 2;
        
        nodes.push({
          id: `person-${person.name}`,
          label: person.name,
          type: 'person',
          x: x,
          y: y,
          data: person,
          size: Math.max(20, (person.skills?.length || 0) * 3 + 15),
          color: '#3B82F6'
        });
      });
    }

    return nodes;
  };

  const calculateRadialLayout = (filteredPeople, filteredCapabilities, filteredSkills, centerX, centerY) => {
    const nodes = [];
    const maxRadius = Math.min(dimensions.width, dimensions.height) * 0.4;

    // People in outer ring
    if (filters.showPeople) {
      const radius = maxRadius;
      filteredPeople.forEach((person, index) => {
        const angle = (index / filteredPeople.length) * 2 * Math.PI;
        const x = centerX + radius * Math.cos(angle);
        const y = centerY + radius * Math.sin(angle);
        
        nodes.push({
          id: `person-${person.name}`,
          label: person.name,
          type: 'person',
          x: x,
          y: y,
          data: person,
          size: Math.max(20, (person.skills?.length || 0) * 3 + 15),
          color: '#3B82F6'
        });
      });
    }

    // Capabilities in middle ring
    if (filters.showCapabilities) {
      const radius = maxRadius * 0.6;
      filteredCapabilities.forEach((capability, index) => {
        const angle = (index / filteredCapabilities.length) * 2 * Math.PI;
        const x = centerX + radius * Math.cos(angle);
        const y = centerY + radius * Math.sin(angle);
        
        const teamCoverage = coverageCalculator.getTeamCoverage(people, capability);
        
        nodes.push({
          id: `capability-${capability.name}`,
          label: capability.name,
          type: 'capability',
          x: x,
          y: y,
          data: capability,
          size: Math.max(15, teamCoverage / 4 + 20),
          color: teamCoverage >= 80 ? '#10B981' : teamCoverage >= 50 ? '#F59E0B' : '#EF4444'
        });
      });
    }

    // Skills in center
    if (filters.showSkills) {
      const radius = maxRadius * 0.2;
      filteredSkills.forEach((skill, index) => {
        const angle = (index / filteredSkills.length) * 2 * Math.PI;
        const x = centerX + radius * Math.cos(angle);
        const y = centerY + radius * Math.sin(angle);
        
        const peopleWithSkill = people.filter(p => p.skills && p.skills.includes(skill)).length;
        const adoptionRate = people.length > 0 ? (peopleWithSkill / people.length) * 100 : 0;
        
        nodes.push({
          id: `skill-${skill}`,
          label: skill,
          type: 'skill',
          x: x,
          y: y,
          data: { skill, adoptionRate, peopleWithSkill },
          size: Math.max(10, adoptionRate / 5 + 12),
          color: adoptionRate >= 75 ? '#8B5CF6' : adoptionRate >= 50 ? '#F59E0B' : '#6B7280'
        });
      });
    }

    return nodes;
  };

  const calculateForceLayout = (filteredPeople, filteredCapabilities, filteredSkills, centerX, centerY) => {
    const nodes = [];
    const spread = Math.min(dimensions.width, dimensions.height) * 0.3;

    // Simple force simulation - spread nodes randomly then apply some organization
    const allNodes = [];
    
    if (filters.showPeople) {
      filteredPeople.forEach(person => {
        allNodes.push({
          id: `person-${person.name}`,
          label: person.name,
          type: 'person',
          data: person,
          size: Math.max(20, (person.skills?.length || 0) * 3 + 15),
          color: '#3B82F6',
          weight: person.skills?.length || 1
        });
      });
    }

    if (filters.showCapabilities) {
      filteredCapabilities.forEach(capability => {
        const teamCoverage = coverageCalculator.getTeamCoverage(people, capability);
        allNodes.push({
          id: `capability-${capability.name}`,
          label: capability.name,
          type: 'capability',
          data: capability,
          size: Math.max(15, teamCoverage / 4 + 20),
          color: teamCoverage >= 80 ? '#10B981' : teamCoverage >= 50 ? '#F59E0B' : '#EF4444',
          weight: teamCoverage / 10
        });
      });
    }

    if (filters.showSkills) {
      filteredSkills.forEach(skill => {
        const peopleWithSkill = people.filter(p => p.skills && p.skills.includes(skill)).length;
        const adoptionRate = people.length > 0 ? (peopleWithSkill / people.length) * 100 : 0;
        
        allNodes.push({
          id: `skill-${skill}`,
          label: skill,
          type: 'skill',
          data: { skill, adoptionRate, peopleWithSkill },
          size: Math.max(10, adoptionRate / 5 + 12),
          color: adoptionRate >= 75 ? '#8B5CF6' : adoptionRate >= 50 ? '#F59E0B' : '#6B7280',
          weight: adoptionRate / 20
        });
      });
    }

    // Position nodes with some randomness but organized by type
    allNodes.forEach((node, index) => {
      const angle = Math.random() * 2 * Math.PI;
      const distance = Math.random() * spread;
      let typeOffset = { x: 0, y: 0 };
      
      // Add slight type-based clustering
      if (node.type === 'person') typeOffset = { x: -spread * 0.3, y: 0 };
      if (node.type === 'capability') typeOffset = { x: 0, y: -spread * 0.3 };
      if (node.type === 'skill') typeOffset = { x: spread * 0.3, y: spread * 0.3 };
      
      const x = centerX + Math.cos(angle) * distance + typeOffset.x;
      const y = centerY + Math.sin(angle) * distance + typeOffset.y;
      
      nodes.push({
        ...node,
        x: Math.max(50, Math.min(dimensions.width - 50, x)),
        y: Math.max(50, Math.min(dimensions.height - 50, y))
      });
    });

    return nodes;
  };

  const createNetwork = () => {
    if (!networkRef.current) return;

    // Clear previous network
    networkRef.current.innerHTML = '';

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '100%');
    svg.setAttribute('height', dimensions.height);
    svg.setAttribute('viewBox', `0 0 ${dimensions.width} ${dimensions.height}`);
    svg.style.cursor = 'grab';
    networkRef.current.appendChild(svg);

    // Create filtered data
    const filteredPeople = people.filter(p => 
      !filters.selectedPerson || p.name === filters.selectedPerson
    );
    const filteredCapabilities = capabilities.filter(c => 
      !filters.selectedCapability || c.name === filters.selectedCapability
    );
    const allSkills = coverageCalculator.getAllSkills(people, capabilities);
    const filteredSkills = allSkills.filter(s => 
      !filters.selectedSkill || s === filters.selectedSkill
    );

    // Use the layout system to calculate node positions
    const nodes = calculateLayout(filteredPeople, filteredCapabilities, filteredSkills);
    const edges = [];

    // Create edges
    if (filters.showConnections) {
      // Person to Capability connections
      filteredPeople.forEach(person => {
        filteredCapabilities.forEach(capability => {
          const coverage = coverageCalculator.calculateCoverage(person, capability);
          if (coverage >= filters.minCoverage) {
            edges.push({
              source: `person-${person.name}`,
              target: `capability-${capability.name}`,
              strength: coverage,
              color: coverage >= 80 ? '#10B981' : coverage >= 50 ? '#F59E0B' : '#EF4444',
              width: Math.max(1, coverage / 20)
            });
          }
        });
      });

      // Person to Skill connections
      if (filters.showSkills) {
        filteredPeople.forEach(person => {
          (person.skills || []).forEach(skill => {
            if (filteredSkills.includes(skill)) {
              edges.push({
                source: `person-${person.name}`,
                target: `skill-${skill}`,
                strength: 100,
                color: '#60A5FA',
                width: 1.5
              });
            }
          });
        });
      }

      // Capability to Skill connections
      if (filters.showSkills) {
        filteredCapabilities.forEach(capability => {
          (capability.required_skills || []).forEach(skill => {
            if (filteredSkills.includes(skill)) {
              edges.push({
                source: `capability-${capability.name}`,
                target: `skill-${skill}`,
                strength: 80,
                color: '#34D399',
                width: 2
              });
            }
          });
        });
      }
    }

    // Draw edges first (so they appear behind nodes)
    edges.forEach(edge => {
      const sourceNode = nodes.find(n => n.id === edge.source);
      const targetNode = nodes.find(n => n.id === edge.target);
      
      if (sourceNode && targetNode) {
        const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        line.setAttribute('x1', sourceNode.x);
        line.setAttribute('y1', sourceNode.y);
        line.setAttribute('x2', targetNode.x);
        line.setAttribute('y2', targetNode.y);
        line.setAttribute('stroke', edge.color);
        line.setAttribute('stroke-width', edge.width);
        line.setAttribute('opacity', '0.7');
        svg.appendChild(line);
      }
    });

    // Draw nodes
    nodes.forEach(node => {
      const group = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      group.style.cursor = 'pointer';
      
      // Node circle
      const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      circle.setAttribute('cx', node.x);
      circle.setAttribute('cy', node.y);
      circle.setAttribute('r', node.size);
      circle.setAttribute('fill', node.color);
      circle.setAttribute('stroke', '#fff');
      circle.setAttribute('stroke-width', '2');
      
      // Hover effect
      circle.addEventListener('mouseenter', () => {
        circle.setAttribute('stroke-width', '4');
        circle.setAttribute('filter', 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))');
      });
      
      circle.addEventListener('mouseleave', () => {
        circle.setAttribute('stroke-width', '2');
        circle.setAttribute('filter', '');
      });

      // Click handler
      group.addEventListener('click', () => {
        setSelectedNode(node);
      });
      
      // Node label
      const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      text.setAttribute('x', node.x);
      text.setAttribute('y', node.y - node.size - 5);
      text.setAttribute('text-anchor', 'middle');
      text.setAttribute('class', 'text-xs font-medium');
      text.setAttribute('fill', '#374151');
      const displayLabel = node.label.length > 12 ? node.label.substring(0, 12) + '...' : node.label;
      text.textContent = displayLabel;
      
      group.appendChild(circle);
      group.appendChild(text);
      svg.appendChild(group);
    });
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleLayoutChange = (newLayout) => {
    if (newLayout === layout || isTransitioning) return;
    
    setIsTransitioning(true);
    setLayout(newLayout);
    
    // Brief transition period
    setTimeout(() => {
      setIsTransitioning(false);
    }, 300);
  };

  const resetFilters = () => {
    setFilters({
      showPeople: true,
      showCapabilities: true,
      showSkills: true,
      showConnections: true,
      selectedPerson: null,
      selectedCapability: null,
      selectedSkill: null,
      minCoverage: 0
    });
  };

  if (!people.length && !capabilities.length) {
    return (
      <div className="bg-white shadow rounded-lg p-6 h-full flex flex-col">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Dynamic Relationship Graph</h2>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center py-8">
            <div className="text-gray-400 text-6xl mb-4">🕸️</div>
            <p className="text-gray-500 mb-4">No data available to display</p>
            <p className="text-sm text-gray-400">Add people and capabilities in the Editor to see the relationship network</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg h-full flex flex-col">
      {/* Header with Controls */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-lg font-medium text-gray-900">Dynamic Relationship Graph</h2>
            <p className="text-sm text-gray-500 mt-1">
              Interactive network showing relationships between people, capabilities, and skills
            </p>
          </div>
          <button
            onClick={resetFilters}
            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
          >
            Reset Filters
          </button>
        </div>

        {/* Layout Selection */}
        <div className="mb-4">
          <label className="text-xs font-medium text-gray-700 block mb-2">Layout</label>
          <div className="flex flex-wrap gap-2">
            {[
              { key: 'circular', label: '⭕ Circular', desc: 'Concentric circles' },
              { key: 'pyramid', label: '🔺 Pyramid', desc: 'Hierarchical pyramid' },
              { key: 'grid', label: '⬜ Grid', desc: 'Organized grid' },
              { key: 'hierarchical', label: '🌳 Hierarchical', desc: 'Tree structure' },
              { key: 'radial', label: '☀️ Radial', desc: 'Radial rings' },
              { key: 'force', label: '🌪️ Force', desc: 'Force-directed' }
            ].map(({ key, label, desc }) => (
              <button
                key={key}
                onClick={() => handleLayoutChange(key)}
                disabled={isTransitioning}
                className={`px-3 py-1 text-xs rounded border transition-colors ${
                  layout === key 
                    ? 'bg-blue-500 text-white border-blue-500' 
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                } ${isTransitioning ? 'opacity-50 cursor-not-allowed' : ''}`}
                title={desc}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Filter Controls */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Node Type Toggles */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-gray-700">Show Elements</label>
            <div className="space-y-1">
              {[
                { key: 'showPeople', label: '👥 People', color: 'bg-blue-500' },
                { key: 'showCapabilities', label: '🎯 Capabilities', color: 'bg-green-500' },
                { key: 'showSkills', label: '⚡ Skills', color: 'bg-purple-500' },
                { key: 'showConnections', label: '🔗 Connections', color: 'bg-gray-500' }
              ].map(({ key, label, color }) => (
                <label key={key} className="flex items-center space-x-2 text-xs">
                  <input
                    type="checkbox"
                    checked={filters[key]}
                    onChange={(e) => handleFilterChange(key, e.target.checked)}
                    className="rounded"
                  />
                  <div className={`w-3 h-3 rounded-full ${color}`} />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Person Filter */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-gray-700">Focus Person</label>
            <select
              value={filters.selectedPerson || ''}
              onChange={(e) => handleFilterChange('selectedPerson', e.target.value || null)}
              className="w-full text-xs border border-gray-300 rounded px-2 py-1"
            >
              <option value="">All People</option>
              {people.map(person => (
                <option key={person.name} value={person.name}>{person.name}</option>
              ))}
            </select>
          </div>

          {/* Capability Filter */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-gray-700">Focus Capability</label>
            <select
              value={filters.selectedCapability || ''}
              onChange={(e) => handleFilterChange('selectedCapability', e.target.value || null)}
              className="w-full text-xs border border-gray-300 rounded px-2 py-1"
            >
              <option value="">All Capabilities</option>
              {capabilities.map(cap => (
                <option key={cap.name} value={cap.name}>{cap.name}</option>
              ))}
            </select>
          </div>

          {/* Coverage Filter */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-gray-700">
              Min Coverage: {filters.minCoverage}%
            </label>
            <input
              type="range"
              min="0"
              max="100"
              step="10"
              value={filters.minCoverage}
              onChange={(e) => handleFilterChange('minCoverage', parseInt(e.target.value))}
              className="w-full"
            />
          </div>
        </div>
      </div>

      {/* Graph Container */}
      <div className="flex-1 p-6 min-h-0">
        <div ref={networkRef} className="w-full h-full border border-gray-200 rounded bg-gray-50"></div>
      </div>

      {/* Legend */}
      <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Node Types</h4>
            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
                <span>People (size = skill count)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                <span>Capabilities (size = team coverage)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-purple-500 rounded-full"></div>
                <span>Skills (size = adoption rate)</span>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Connection Colors</h4>
            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <div className="w-6 h-1 bg-green-500"></div>
                <span>High Coverage (80%+)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-6 h-1 bg-yellow-500"></div>
                <span>Medium Coverage (50-79%)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-6 h-1 bg-red-500"></div>
                <span>Low Coverage (&lt;50%)</span>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 mb-2">Interactions</h4>
            <div className="space-y-1">
              <span>• Click nodes for details</span>
              <span>• Hover for highlights</span>
              <span>• Use filters to focus</span>
              <span>• Resize window to expand</span>
            </div>
          </div>
        </div>
      </div>

      {/* Node Details Modal */}
      {selectedNode && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-96 overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">{selectedNode.label}</h3>
              <button
                onClick={() => setSelectedNode(null)}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <div className={`w-4 h-4 rounded-full`} style={{ backgroundColor: selectedNode.color }} />
                <span className="font-medium capitalize">{selectedNode.type}</span>
              </div>
              
              {selectedNode.type === 'person' && (
                <div>
                  <p><strong>Skills:</strong> {selectedNode.data.skills?.join(', ') || 'None'}</p>
                  <p><strong>Certifications:</strong> {selectedNode.data.certifications?.join(', ') || 'None'}</p>
                </div>
              )}
              
              {selectedNode.type === 'capability' && (
                <div>
                  <p><strong>Description:</strong> {selectedNode.data.description || 'No description'}</p>
                  <p><strong>Required Skills:</strong> {selectedNode.data.required_skills?.join(', ') || 'None'}</p>
                  <p><strong>Team Coverage:</strong> {coverageCalculator.getTeamCoverage(people, selectedNode.data)}%</p>
                </div>
              )}
              
              {selectedNode.type === 'skill' && (
                <div>
                  <p><strong>Adoption Rate:</strong> {Math.round(selectedNode.data.adoptionRate)}%</p>
                  <p><strong>People with Skill:</strong> {selectedNode.data.peopleWithSkill} / {people.length}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GraphView;